import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type SortingState } from "@tanstack/react-table"
import { type FC, useEffect, useMemo, useRef, useState } from "react"

import { FilterDrawer, UsersFilterChips, UsersTable } from "../components"
import { type UserSortableAccessorKey, accessorKeyToUserSortField, userSortFieldToAccessoryKey } from "../constants"
import { type UsersSearchFilters, useUsersSearchStore } from "../stores"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import { SortingOrder, type UserSortField } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"

const UsersContainer: FC = () => {
  const navigate = useNavigate()

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const currentSearch = useSearch({ from: "/app/admin/system-admin/_layout/users" })

  const { query, field, order, isActive, roleIds } = currentSearch

  const data = useUsersSearchStore(({ data }) => data)
  const fetching = useUsersSearchStore(({ fetching }) => fetching)
  const error = useUsersSearchStore(({ error }) => error)
  const executeSearch = useUsersSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useUsersSearchStore(({ loadMore }) => loadMore)
  const clearStore = useUsersSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        isActive: true,
      }),
      to: "/app/admin/system-admin/users",
      replace: true,
    })
  }, [navigate])

  useEffect(() => {
    const searchFilters: UsersSearchFilters = {
      query,
      field,
      order,
      isActive,
      roleIds,
    }
    executeSearch(queryClient, searchFilters)
  }, [query, field, order, executeSearch, isActive, roleIds])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: UsersSearchFilters = {
      query,
      field: field as UserSortField | undefined,
      order,
      isActive,
      roleIds,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = userSortFieldToAccessoryKey.get(field as UserSortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as UserSortableAccessorKey
      const field = accessorKeyToUserSortField.get(key)

      if (!field) {
        return
      }

      void navigate({
        search: (prev) => ({
          ...prev,
          query,
          field,
          order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
          isActive,
          roleIds,
        }),
        to: "/app/admin/system-admin/users",
        replace: true,
      })
    }
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={setFilterDrawerOpen} />

      <TableHeaderActions currentSearch={currentSearch} placeholder='Search by Name'>
        <Button
          appearance='outlined'
          value='Filter'
          startIcon={<FilterAltOutlined />}
          onClick={() => setFilterDrawerOpen(true)}
          rootElementName='button'
        />
      </TableHeaderActions>

      <Box data-testid='users-chips' className='flex flex-wrap items-center gap-4'>
        <UsersFilterChips />
      </Box>

      <Box className='grow overflow-hidden'>
        <UsersTable
          data={data}
          scrollHandler={scrollHandler}
          tableContainerRef={tableContainerRef}
          handleSortingChange={handleSortingChange}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>
    </Box>
  )
}

export default UsersContainer
